import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { collection, query, orderBy, limit, doc, onSnapshot, getDoc, getDocs } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { useUnit } from '@/contexts/UnitContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { normalizeCreditsSystemModeFromPlatformData } from '@/lib/creditsPlatformMode';
import type { CreditPackRow } from '@masonic-tools/shared-types';
import { DEFAULT_CREDIT_PACK_ROWS } from '@masonic-tools/shared-types';
import { cn, formatCurrency } from '@/lib/utils';
import { ArrowLeft, ChevronDown, Loader2, Wallet, Info } from 'lucide-react';
import { showSuccess, showError, showInfo } from '@/lib/swal';
import * as Sentry from '@sentry/react';
import {
  saveSlickPayReturnOrigin,
  savePendingSlickPayOrder,
  slickPayCreditsOriginKey,
  type SlickPayResultState,
} from '@/lib/slickpay-return';
type CreditTx = Record<string, unknown> & { id: string };

function formatDateTime(ts: unknown): { date: string; time: string } {
  if (!ts) return { date: '—', time: '—' };
  const d: Date | null =
    ts && typeof (ts as any).toDate === 'function'
      ? (ts as any).toDate()
      : ts instanceof Date
        ? ts
        : typeof ts === 'string' || typeof ts === 'number'
          ? new Date(ts)
          : null;
  if (!d || isNaN(d.getTime())) return { date: '—', time: '—' };
  return {
    date: d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/London' }),
    time: d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/London' }),
  };
}

function txTypeLabel(type: unknown): string {
  const s = String(type ?? 'entry');
  const map: Record<string, string> = {
    purchase: 'Purchase',
    credit_purchase: 'Purchase',
    consume: 'Consume (RSVP)',
    debit: 'Consume (RSVP)',
    adjustment: 'Adjustment',
    refund: 'Refund',
    return: 'Credit Return',
  };
  return map[s] ?? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function txBadgeVariant(type: unknown): 'default' | 'secondary' | 'destructive' | 'outline' {
  const s = String(type ?? '');
  if (s === 'purchase' || s === 'credit_purchase' || s === 'refund' || s === 'return') return 'default';
  if (s === 'consume' || s === 'debit') return 'destructive';
  return 'secondary';
}

/** Render a labelled row in the details dialog */
function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value && value !== 0) return null;
  return (
    <div className="grid grid-cols-[160px_1fr] gap-2 border-b py-2 last:border-b-0">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="text-sm break-all">{value}</span>
    </div>
  );
}

export default function UnitCreditsOverviewPage() {
  const { unitId } = useParams<{ unitId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userProfile, currentUser } = useAuth();
  const { currentUnit } = useUnit();
  const navigate = useNavigate();
  const location = useLocation();

  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [creditTxs, setCreditTxs] = useState<CreditTx[]>([]);
  const [creditPacks, setCreditPacks] = useState<CreditPackRow[]>(DEFAULT_CREDIT_PACK_ROWS);
  /** Pack id currently opening checkout; null when idle */
  const [checkoutPackId, setCheckoutPackId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState<string>('Unit');
  const [selectedTx, setSelectedTx] = useState<CreditTx | null>(null);
  // Map uid -> display name, lazily populated when dialog opens
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [creditsPurchaseProvider, setCreditsPurchaseProvider] = useState<'stripe' | 'slickpay'>('stripe');
  const [howCreditsCollapsed, setHowCreditsCollapsed] = useState(false);

  const howCreditsStorageKey =
    currentUser?.uid != null && currentUser.uid !== ''
      ? `dining:credits-how-card-collapsed:${currentUser.uid}`
      : null;

  useLayoutEffect(() => {
    if (!howCreditsStorageKey || typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(howCreditsStorageKey);
      if (raw === 'true') setHowCreditsCollapsed(true);
      else if (raw === 'false') setHowCreditsCollapsed(false);
    } catch {
      /* ignore */
    }
  }, [howCreditsStorageKey]);

  function toggleHowCreditsCollapsed() {
    setHowCreditsCollapsed((prev) => {
      const next = !prev;
      const uid = currentUser?.uid;
      if (uid) {
        try {
          localStorage.setItem(`dining:credits-how-card-collapsed:${uid}`, String(next));
        } catch {
          /* ignore */
        }
      }
      return next;
    });
  }

  const userRole = userProfile?.units?.find((u) => u.unitId === unitId)?.role;
  const isAdmin = userProfile?.isAdmin === true;
  const canBuyCredits =
    userRole === 'Secretary' || userRole === 'Treasurer' || isAdmin;

  // Protect page
  useEffect(() => {
    if (userRole !== 'Secretary' && userRole !== 'Treasurer' && !isAdmin) {
      navigate(`/unit/${unitId}/dashboard`);
    }
  }, [userRole, isAdmin, navigate, unitId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'systemSettings', 'platform'));
        const p = snap.data()?.creditsPaymentProvider;
        if (!cancelled) {
          setCreditsPurchaseProvider(p === 'slickpay' ? 'slickpay' : 'stripe');
        }
      } catch {
        if (!cancelled) setCreditsPurchaseProvider('stripe');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle redirect query params from Stripe
  useEffect(() => {
    const cp = searchParams.get('creditPurchase');
    if (cp === 'success') {
      showSuccess('Credit purchase completed.');
      setSearchParams((p) => { const n = new URLSearchParams(p); n.delete('creditPurchase'); return n; }, { replace: true });
    } else if (cp === 'cancelled') {
      showError('Credit purchase was cancelled.');
      setSearchParams((p) => { const n = new URLSearchParams(p); n.delete('creditPurchase'); return n; }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // SlickPay Open Banking return: show toast after callback redirect
  useEffect(() => {
    const state = location.state as SlickPayResultState | null;
    if (!state?.slickpayResult) return;

    const message =
      state.message ||
      (state.slickpayResult === 'success'
        ? 'Credit purchase completed.'
        : state.slickpayResult === 'pending'
          ? 'Your Open Banking credit purchase is still confirming. Check back shortly.'
          : 'Your Open Banking credit purchase did not complete. Please try again.');

    if (state.slickpayResult === 'success') {
      showSuccess(message);
    } else if (state.slickpayResult === 'pending') {
      showInfo(message);
    } else {
      showError(message);
    }

    navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
  }, [location.state, location.pathname, location.search, navigate]);

  // Poll Open Banking credit purchases when callback page was missed (mirrors invoice dashboard backstop).
  useEffect(() => {
    if (!unitId || creditsPurchaseProvider !== 'slickpay' || !canBuyCredits) return;

    const LOCALSTORAGE_KEY = `slickpay_credits_${unitId}`;

    const pollPending = async () => {
      const poIds = new Set<string>();
      const stored = localStorage.getItem(LOCALSTORAGE_KEY);
      if (stored) poIds.add(stored);

      try {
        const pendingSnap = await getDocs(
          collection(db, 'units', unitId, 'pendingCreditPurchases'),
        );
        pendingSnap.forEach((d) => {
          const status = d.data().status;
          if (status === 'pending' || status === 'complete') {
            poIds.add(d.id);
          }
        });
      } catch (e) {
        console.error('Failed to load pending credit purchases', e);
      }

      if (poIds.size === 0) return;

      const checkStatus = httpsCallable<
        { unitId: string; poId: string },
        { success?: boolean; status?: string; credits?: number }
      >(functions, 'checkCreditPurchaseSlickPayStatus');

      for (const poId of poIds) {
        try {
          const result = await checkStatus({ unitId, poId });
          const data = result.data;
          if (data.success && data.status === 'PAID') {
            localStorage.removeItem(LOCALSTORAGE_KEY);
            if (typeof data.credits === 'number') {
              showSuccess(`${data.credits} credits added to your unit balance.`);
            } else {
              showSuccess('Credit purchase completed.');
            }
          }
        } catch (e) {
          console.error('Error checking credit purchase payment status', e);
        }
      }
    };

    void pollPending();
    const interval = setInterval(() => {
      void pollPending();
    }, 30_000);

    return () => clearInterval(interval);
  }, [unitId, creditsPurchaseProvider, canBuyCredits]);

  // Live credit balance
  useEffect(() => {
    if (!unitId) return;
    const unsub = onSnapshot(doc(db, 'units', unitId), (snap) => {
      const v = snap.data()?.creditBalance;
      setCreditBalance(typeof v === 'number' ? v : 0);
    });
    return () => unsub();
  }, [unitId]);

  // Live transaction list
  useEffect(() => {
    if (!unitId) return;
    const q = query(collection(db, 'units', unitId, 'creditTransactions'), orderBy('createdAt', 'desc'), limit(100));
    const unsub = onSnapshot(q, (snap) => {
      setCreditTxs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [unitId]);

  // Load credit packs from platform settings
  useEffect(() => {
    const loadPacks = async () => {
      try {
        const s = await getDoc(doc(db, 'systemSettings', 'platform'));
        
        const mode = normalizeCreditsSystemModeFromPlatformData(s.data() as Record<string, unknown> | undefined);
        if (mode === 'disabled' || (mode === 'admin_only' && !isAdmin)) {
          navigate(`/unit/${unitId}/dashboard`);
          return;
        }

        const raw = s.data()?.creditPacks;
        let packs: CreditPackRow[];
        if (Array.isArray(raw) && raw.length > 0) {
          const cleaned = raw.filter(
            (p) => p && typeof p.id === 'string' && typeof p.credits === 'number' && typeof p.pricePence === 'number'
          ) as CreditPackRow[];
          packs = cleaned.length > 0 ? cleaned : [...DEFAULT_CREDIT_PACK_ROWS];
        } else {
          packs = [...DEFAULT_CREDIT_PACK_ROWS];
        }

        setCreditPacks(packs);
      } catch {
        setCreditPacks([...DEFAULT_CREDIT_PACK_ROWS]);
      }
    };
    loadPacks();
  }, [isAdmin, navigate, unitId]);

  // Entity name
  useEffect(() => {
    const fetchUnitType = async () => {
      if (!currentUnit) { setEntityName('Unit'); return; }
      if ((currentUnit as any).entityName) { setEntityName((currentUnit as any).entityName); return; }
      if (!currentUnit?.typeId) { setEntityName('Unit'); return; }
      try {
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          const unitType = (settingsDoc.data().unitTypes || []).find((ut: any) => ut.id === currentUnit.typeId);
          setEntityName(unitType?.entityName ?? 'Unit');
        } else { setEntityName('Unit'); }
      } catch { setEntityName('Unit'); }
    };
    fetchUnitType();
  }, [currentUnit]);

  // Resolve the authorised-by uid to a display name for all transactions
  useEffect(() => {
    if (creditTxs.length === 0) return;

    const fetchNames = async () => {
      const uidsToFetch = new Set<string>();
      creditTxs.forEach((tx) => {
        const uid = (tx.metadata as any)?.authorisedBy || (tx as any).authorisedBy || (tx as any).createdBy;
        if (uid && typeof uid === 'string' && !userNames[uid]) {
          uidsToFetch.add(uid);
        }
      });

      if (uidsToFetch.size === 0) return;

      const newNames: Record<string, string> = {};
      await Promise.all(
        Array.from(uidsToFetch).map(async (uid) => {
          try {
            const snap = await getDoc(doc(db, 'users', uid));
            if (snap.exists()) {
              const d = snap.data();
              newNames[uid] = d.name || `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.email || uid;
            } else {
              newNames[uid] = uid;
            }
          } catch (e) {
            newNames[uid] = uid;
          }
        })
      );

      if (Object.keys(newNames).length > 0) {
        setUserNames((prev) => ({ ...prev, ...newNames }));
      }
    };

    fetchNames();
  }, [creditTxs, userNames]);

  // ─── helpers ─────────────────────────────────────────────────────────────────

  function getAuthorisedBy(tx: CreditTx): string {
    const uid = (tx.metadata as any)?.authorisedBy || (tx as any).authorisedBy || (tx as any).createdBy;
    if (!uid) {
      const type = typeof tx.type === 'string' ? tx.type : String((tx as any).kind ?? 'entry');
      if (type === 'consume' || type === 'debit') return 'System';
      return 'System / Payment provider';
    }
    return userNames[uid] || uid;
  }

  function getDelta(tx: CreditTx): { display: string; positive: boolean } {
    const delta = typeof tx.delta === 'number' ? tx.delta : Number((tx as any).amountCredits ?? 0);
    const isNeg = delta < 0 || (tx as any).direction === 'debit' || (tx as any).kind === 'debit';
    const abs = Math.abs(delta);
    return { display: `${isNeg ? '-' : '+'}${abs}`, positive: !isNeg };
  }

  const checkoutBusy = checkoutPackId !== null;

  async function startPackCheckout(packId: string) {
    if (!unitId || checkoutBusy) return;
    setCheckoutPackId(packId);
    let navigated = false;
    try {
      const fnName =
        creditsPurchaseProvider === 'slickpay'
          ? 'createCreditPurchaseSlickPaySession'
          : 'createCreditPurchaseCheckoutSession';
      const fn = httpsCallable(functions, fnName);
      const res = await fn({ unitId, packId });
      const { url, sessionId } = res.data as { url?: string; sessionId?: string };
      if (url) {
        // Store poId so the callback page can retrieve it after the redirect.
        if (creditsPurchaseProvider === 'slickpay' && sessionId && unitId) {
          try {
            localStorage.setItem(`slickpay_credits_${unitId}`, sessionId);
            saveSlickPayReturnOrigin(slickPayCreditsOriginKey(unitId));
            savePendingSlickPayOrder(sessionId);
          } catch {
            /* ignore quota errors */
          }
        }
        navigated = true;
        window.location.href = url;
        return;
      }
      showError('Could not start checkout.');
    } catch (e: unknown) {
      console.error(e);
      Sentry.captureException(e, {
        tags: {
          area: 'payment',
          payment_method: creditsPurchaseProvider === 'slickpay' ? 'slickpay' : 'stripe',
          flow: 'credit_purchase',
        },
        extra: { unitId, packId },
      });
      const raw =
        e && typeof e === 'object' && 'message' in e
          ? String((e as { message: unknown }).message)
          : '';
      const msg = raw.replace(/^Firebase:\s*/, '').replace(/\s*\(auth\/[^)]+\)\s*\.?$/i, '').trim();
      if (msg.includes('permission-denied') || msg.includes('Only Secretary or Treasurer')) {
        showError(
          'You need Secretary, Treasurer, or platform administrator rights to buy credits for this unit.',
        );
      } else if (msg) {
        showError(msg);
      } else {
        showError('Could not start checkout.');
      }
    } finally {
      if (!navigated) setCheckoutPackId(null);
    }
  }

  // ─── render ───────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <Header />
      <main className="flex w-full min-w-0 flex-1 overflow-x-hidden py-6 sm:py-8">
        <DashboardContentContainer>
          {/* Page header */}
          <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
            <div className="min-w-0 flex-1">
              <Button
                variant="ghost" size="sm"
                className="mb-2 h-auto max-w-full justify-start whitespace-normal px-3 text-left text-sm sm:text-base"
                onClick={() => navigate(`/unit/${unitId}/dashboard`)}
              >
                <ArrowLeft className="mr-2 h-4 w-4 shrink-0" />
                Back to {entityName} Dashboard
              </Button>
              <h1 className="break-words text-2xl font-bold tracking-tight sm:text-3xl">Credits Overview</h1>
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                if (!unitId) return;
                try {
                  const fn = httpsCallable(functions, 'generateUnitCreditStatement');
                  const res = await fn({
                    unitId,
                    fromDate: new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toISOString(),
                    toDate: new Date().toISOString(),
                  });
                  const csvData = (res.data as any).csv;
                  const blob = new Blob([csvData], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `unit-${unitId}-credit-statement.csv`;
                  a.click();
                } catch { showError('Could not generate statement.'); }
              }}
            >
              Export Statement
            </Button>
          </div>

          <Card className="mb-8">
            <CardHeader className="pb-3">
              <button
                type="button"
                onClick={toggleHowCreditsCollapsed}
                aria-expanded={!howCreditsCollapsed}
                className={cn(
                  'flex w-full items-start gap-3 rounded-md text-left outline-none transition-colors',
                  'hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  '-m-1 p-1 sm:-m-2 sm:p-2',
                )}
              >
                <div className="min-w-0 flex-1 space-y-1.5">
                  <CardTitle className="text-lg">How credits work</CardTitle>
                  <CardDescription>
                    Masonic Tools credits belong to this unit as a whole, not to individual members or officers.
                  </CardDescription>
                </div>
                <ChevronDown
                  className={cn(
                    'mt-0.5 h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200',
                    howCreditsCollapsed ? '-rotate-90' : 'rotate-0',
                  )}
                  aria-hidden
                />
              </button>
            </CardHeader>
            {!howCreditsCollapsed && (
              <CardContent className="space-y-4 pt-0 text-sm text-muted-foreground">
                <div className="space-y-2">
                  <p className="font-medium text-foreground">What they pay for</p>
                  <p>
                    Each unit keeps one balance on record. When an event is set to charge credits per RSVP, confirming a
                    place consumes that many credits from the unit balance once the RSVP is confirmed (not for declined,
                    waitlisted, or apology-only registrations). Any guest or member who confirms under that unit draws from
                    the same pool.
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-foreground">When credits go back on the balance</p>
                  <p>
                    If an RSVP that already used credits is removed through the proper delete flow, those credits are
                    returned to the unit and appear in your history as an adjustment linked to that RSVP.
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-foreground">When credits are not automatically refunded</p>
                  <p>
                    Changing details, switching someone to apologies, or other edits that leave the RSVP in place do not
                    automatically reverse a past debit. Purchases of credit packs are payments for platform usage and are
                    not cash-refunded through this screen; resolve billing questions with your treasurer or platform support.
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-foreground">Negative balance</p>
                  <p>
                    The balance can fall below zero if usage runs ahead of purchases. While negative, data exports and some
                    bulk actions stay blocked until the balance is brought back to zero or above (usually by buying credits).
                  </p>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Balance card */}
          <Card className="mb-8">
            <CardHeader>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2">
                  <Wallet className="h-5 w-5" />
                  <CardTitle className="text-lg">Masonic Tools credits</CardTitle>
                </div>
                <Badge
                  variant={creditBalance !== null && creditBalance < 0 ? 'destructive' : 'secondary'}
                  className="text-sm px-3 py-1"
                >
                  Current Balance: {creditBalance === null ? '…' : creditBalance}
                </Badge>
              </div>
              <CardDescription>
                Credits pay for confirmed RSVP places on credit-charging events. Choose a pack below to open secure
                checkout with{' '}
                {creditsPurchaseProvider === 'slickpay'
                  ? 'SlickPay (Open Banking).'
                  : 'Stripe (card).'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {creditBalance !== null && creditBalance < 0 && (
                <Alert variant="destructive">
                  <AlertDescription>
                    This unit has a negative credit balance. Exports and some bulk actions may be paused until the balance is cleared.
                  </AlertDescription>
                </Alert>
              )}

              {/* Buy credits */}
              {canBuyCredits && (
                <div className="space-y-3">
                  <div className="text-sm font-medium">Purchase pack</div>
                  <div
                    className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
                    role="group"
                    aria-label="Credit packs; each opens checkout"
                  >
                    {creditPacks.map((p) => {
                      const labelTrim = p.label?.trim();
                      const title = labelTrim || `${p.credits} credits`;
                      const subtitle =
                        labelTrim && labelTrim !== `${p.credits} credits`
                          ? `${p.credits} credits`
                          : null;
                      const price = formatCurrency(p.pricePence / 100);
                      const loading = checkoutPackId === p.id;
                      const desc = p.description?.trim();
                      return (
                        <Button
                          key={p.id}
                          type="button"
                          variant="ghost"
                          disabled={checkoutBusy}
                          className={cn(
                            'relative h-auto min-h-[9.5rem] flex-col items-stretch justify-between gap-3 overflow-hidden rounded-xl border-2 p-6 text-left text-foreground shadow-sm ring-offset-background',
                            'border-primary/20 bg-gradient-to-br from-primary/[0.08] via-muted/50 to-accent/[0.18]',
                            'dark:border-primary/30 dark:from-primary/[0.22] dark:via-card dark:to-accent/[0.14]',
                            'transition-[transform,box-shadow,border-color,background-image] duration-200',
                            'hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/15',
                            'hover:from-primary/[0.12] hover:via-muted/60 hover:to-accent/[0.26]',
                            'dark:hover:shadow-accent/20 dark:hover:from-primary/[0.28] dark:hover:to-accent/[0.2]',
                            'hover:text-foreground',
                            'disabled:pointer-events-none disabled:opacity-60 disabled:hover:translate-y-0',
                            'before:pointer-events-none before:absolute before:inset-0 before:z-0 before:rounded-[0.65rem] before:bg-gradient-to-t before:from-transparent before:to-white/[0.12] before:content-[\'\']',
                            'dark:before:to-white/[0.06]',
                          )}
                          onClick={() => void startPackCheckout(p.id)}
                        >
                          {loading ? (
                            <span className="relative z-10 flex flex-1 flex-col items-center justify-center gap-3 py-4">
                              <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
                              <span className="text-base font-medium">Redirecting to checkout…</span>
                            </span>
                          ) : (
                            <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-between gap-3">
                              <div className="space-y-1">
                                <span className="block text-lg font-semibold leading-snug tracking-tight sm:text-xl">
                                  {title}
                                </span>
                                {subtitle ? (
                                  <span className="text-sm text-muted-foreground">{subtitle}</span>
                                ) : null}
                              </div>
                              <span className="text-2xl font-bold tracking-tight text-primary sm:text-3xl">
                                {price}
                              </span>
                              {desc ? (
                                <p className="line-clamp-4 text-sm leading-relaxed text-muted-foreground">{desc}</p>
                              ) : null}
                            </div>
                          )}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Transaction history */}
              <div className="mt-8">
                <h3 className="text-lg font-medium mb-4">Transaction History</h3>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Authorised by</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Credits</TableHead>
                        <TableHead className="text-right">Balance after</TableHead>
                        <TableHead className="text-center">Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {creditTxs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-muted-foreground text-center py-6">
                            No transactions yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        creditTxs.map((tx) => {
                          const { date, time } = formatDateTime(tx.createdAt);
                          const type = typeof tx.type === 'string' ? tx.type : String((tx as any).kind ?? 'entry');
                          const { display: deltaDisplay, positive } = getDelta(tx);
                          const authorisedBy = getAuthorisedBy(tx);

                          // Short description for the table row
                          let description = '';
                          if (type === 'purchase' || type === 'credit_purchase') {
                            const packId = (tx.metadata as any)?.packId || (tx as any).packId;
                            const credits = (tx.metadata as any)?.credits ?? (tx as any).delta;
                            description = [packId, credits ? `${credits} credits` : null].filter(Boolean).join(' · ');
                          } else if (type === 'consume' || type === 'debit') {
                            const rsvpId = (tx.metadata as any)?.rsvpId || (tx as any).rsvpId;
                            const eventId = (tx.metadata as any)?.eventId || (tx as any).eventId;
                            const txDesc = (tx.metadata as any)?.description || (tx as any).description;
                            if (txDesc) {
                              description = txDesc;
                            } else {
                              description = rsvpId ? `RSVP ${rsvpId.slice(0, 8)}…` : (eventId ? `Event ${eventId.slice(0, 8)}…` : '');
                            }
                          } else if (type === 'adjustment' || type === 'return') {
                            description = String((tx as any).reason || '');
                          }

                          return (
                            <TableRow key={tx.id}>
                              <TableCell className="whitespace-nowrap text-sm">{date}</TableCell>
                              <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{time}</TableCell>
                              <TableCell>
                                <Badge variant={txBadgeVariant(type)} className="whitespace-nowrap text-xs">
                                  {txTypeLabel(type)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm max-w-[140px] truncate" title={authorisedBy}>
                                {authorisedBy}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate" title={description}>
                                {description || '—'}
                              </TableCell>
                              <TableCell className={`text-right tabular-nums font-medium ${positive ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
                                {deltaDisplay}
                              </TableCell>
                              <TableCell className="text-right tabular-nums text-sm">
                                {tx.balanceAfter !== undefined ? String(tx.balanceAfter) : '—'}
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  title="View details"
                                  onClick={() => setSelectedTx(tx)}
                                >
                                  <Info className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </DashboardContentContainer>
      </main>

      {/* Transaction Detail Dialog */}
      <Dialog open={!!selectedTx} onOpenChange={(open) => { if (!open) setSelectedTx(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Transaction Details
            </DialogTitle>
          </DialogHeader>

          {selectedTx && (() => {
            const tx = selectedTx;
            const { date, time } = formatDateTime(tx.createdAt);
            const type = typeof tx.type === 'string' ? tx.type : String((tx as any).kind ?? 'entry');
            const { display: deltaDisplay, positive } = getDelta(tx);
            const meta = (tx.metadata as Record<string, unknown>) ?? {};
            const authorisedBy = getAuthorisedBy(tx);

            return (
              <div className="space-y-1 pt-2">
                <DetailRow label="Transaction ID" value={tx.id} />
                <DetailRow label="Date" value={date} />
                <DetailRow label="Time" value={time} />
                <DetailRow label="Type" value={<Badge variant={txBadgeVariant(type)} className="text-xs">{txTypeLabel(type)}</Badge>} />
                <DetailRow
                  label="Credits"
                  value={
                    <span className={`font-semibold ${positive ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
                      {deltaDisplay}
                    </span>
                  }
                />
                <DetailRow label="Balance after" value={tx.balanceAfter !== undefined ? String(tx.balanceAfter) : undefined} />
                <DetailRow label="Authorised by" value={authorisedBy} />
                <DetailRow label="Idempotency key" value={(tx as any).idempotencyKey} />

                {/* Purchase-specific fields */}
                {(type === 'purchase' || type === 'credit_purchase') && (
                  <>
                    <DetailRow label="Pack ID" value={String(meta.packId || (tx as any).packId || '')} />
                    <DetailRow label="Payment method" value={String(meta.paymentMethod || (tx as any).provider || '')} />
                    <DetailRow
                      label="Amount paid"
                      value={
                        meta.pricePence || (tx as any).pricePence
                          ? formatCurrency(Number(meta.pricePence || (tx as any).pricePence) / 100)
                          : undefined
                      }
                    />
                    <DetailRow label="Payment reference" value={String(meta.stripeCheckoutSessionId || meta.slickpayPoId || (tx as any).providerPaymentId || '')} />
                  </>
                )}

                {/* RSVP consume fields */}
                {(type === 'consume' || type === 'debit') && (
                  <>
                    <DetailRow label="RSVP" value={
                      meta.rsvpId || (tx as any).rsvpId ? (
                        <a 
                          href={`/unit/${unitId}/event/${meta.eventId || (tx as any).eventId}/rsvp/${meta.rsvpId || (tx as any).rsvpId}/dashboard`} 
                          target="_blank" 
                          rel="noreferrer"
                          className="text-primary hover:underline focus:underline inline-flex items-center"
                        >
                          {meta.description || meta.rsvpName || (tx as any).description || String(meta.rsvpId || (tx as any).rsvpId || '')}
                        </a>
                      ) : (
                        '—'
                      )
                    } />
                    <DetailRow label="RSVP ID" value={String(meta.rsvpId || (tx as any).rsvpId || '')} />
                    <DetailRow label="Event ID" value={String(meta.eventId || (tx as any).eventId || '')} />
                    <DetailRow label="Credits per RSVP" value={String(meta.creditsPerRsvp ?? (tx as any).creditsPerRsvp ?? '')} />
                  </>
                )}

                {/* Adjustment / return fields */}
                {(type === 'adjustment' || type === 'return') && (
                  <DetailRow label="Reason" value={String((tx as any).reason || '')} />
                )}

                {/* Raw metadata dump for transparency */}
                {Object.keys(meta).length > 0 && (
                  <div className="mt-4 rounded-md border bg-muted/40 p-3">
                    <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Full metadata</p>
                    <pre className="text-xs whitespace-pre-wrap break-all leading-relaxed">
                      {JSON.stringify(meta, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
